import _ from 'lodash';
import Element from './Element';
import Slide from './Slide';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import PlayableMixin from './mixins/PlayableMixin';
import ContainableMixin from './mixins/ContainableMixin';
import ClickableMixin from './mixins/ClickableMixin';
import mixin from '../privates/utils/mixin';
import reporter from '../privates/reporter';
import {generateError} from '../privates/validations/errors';
import {setStageUnfriendlyFunctions} from '../privates/utils/livePreview';


const validateSlideInfo = (compType, slideInfo, slidesCount, slideIndex, slideshowID) => {
    if (!validate({
        compType,
        functionName: 'changeSlide',
        dataToValidate: _.isNil(slideInfo) ? {} : {slideInfo},
        shouldCheckMandatoryFields: true,
        compName: slideshowID
    })) {
        return false;
    }
    if (_.isObject(slideInfo) && !(slideInfo instanceof Slide)) {
        reporter.reportSdkError(generateError.error_type({
            functionName: 'changeSlide',
            propertyName: 'slideInfo',
            wrongValue: slideInfo,
            expectedType: 'slide,integer'
        }));
        return false;
    }

    if (slideIndex < 0 || slideIndex >= slidesCount) {
        if (slideInfo instanceof Slide) {
            reporter.reportSdkError(generateError.error_slide_input({
                propertyName: 'slideInfo',
                functionName: 'changeSlide',
                minValue: 0,
                maxValue: slidesCount - 1,
                slideshowID,
                wrongValue: slideInfo.id}));

        } else {
            reporter.reportSdkError(generateError.error_slide_input({
                propertyName: 'slideInfo',
                functionName: 'changeSlide',
                minValue: 0,
                maxValue: slidesCount - 1,
                slideshowID,
                wrongValue: slideIndex
            }));
        }
        return false;
    }

    return true;
};

const computeSlideIndex = (RMI, compId, slideInfo) => {
    let slideIndex = -1;
    if (slideInfo instanceof Slide) {
        const slideCompId = members._compId.get(slideInfo);

        slideIndex = RMI.getChildren(compId).indexOf(slideCompId);
    } else {
        slideIndex = slideInfo;
    }

    return slideIndex;
};

const getSlidesCount = (RMI, compId) => RMI.getChildren(compId).length;

/**
 * @class Slideshow
 * @summary Slideshows are playable containers for slides.
 * @description
 *  You can open the Velo Properties & Events panel for the `Slideshow` element
 *  but not for its individual [`Slide`]($w.Slide.html) elements.
 *
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.PlayableMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.ContainableMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.BoxSlideShow
 * @viewername wysiwyg.viewer.components.StripContainerSlideShow
 * @definitionId wysiwyg.viewer.components.BoxSlideShow
 * @definitionId wysiwyg.viewer.components.StripContainerSlideShow
 * @tagname box-slideshow
 */
class Slideshow extends Element {

    /**
     * @function onChange
     * @syntax
     * function onChange(handler: EventHandler): Slideshow
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when the slideshow moves to a new slide.
     * @description
     *  A slideshow moves to a new slide through user actions, such as clicking
     *  on navigation or slide buttons, or programmatically, using functions
     *  such as [changeSlide()](#changeSlide), [play()](#play), [previous( )](#previous), and [next( )](#next).
     *
     * > **Note:** You can see the `Slideshow` element in the Velo Properties & Events panel, but 
     *  its [`Slide`]($w.Slide.html) elements are not visible there.
     * >
     * >
     * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @param {$w.EventHandler} eventHandler The name of the function or
     *  the function expression to run when the slideshow moves to a new slide.
     * @returns {$w.Slideshow} The slideshow on which the event is now registered.
     * @snippet [Slideshow-onChange.es6=Get the index of the slide to which the slideshow moved]
     * @see [changeSlide( )](#changeSlide)
     * @see [previous( )](#previous)
     * @see [next( )](#next)
     * @see [autoplay](#autoplay)
     * @memberof $w.Slideshow
     * @eventtype change
     * @eventhandler
     */
    onChange(callback) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onChange',
            dataToValidate: {onChange: callback},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().CHANGE, controllerId, callback);
        return this;
    }

    /**
     * @member currentSlide
     * @label property
     * @syntax
     * get currentSlide(): Slide
     * @summary Gets the slideshow's current slide.
     * @snippet [Slideshow-currentSlide.es6=Get the current slide and its name]
     * @type {$w.Slide}
     * @see [currentIndex](#currentindex)
     * @memberof $w.Slideshow
     * @instance
     * @readonly
     */
    get currentSlide() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const wixSelector = members._wixSelector.get(this);
        const controllerId = members._controllerId.get(this);

        const currentSlideIndex = RMI.getState(compId).currentIndex;
        const currentSlideCompId = RMI.getChildren(compId)[currentSlideIndex];
        const currentSlideType = RMI.getType(currentSlideCompId);

        return wixSelector.getInstance(RMI, currentSlideType, currentSlideCompId, controllerId);
    }

    /**
     * @function changeSlide
     * @syntax
     * function changeSlide(slideReference: Number | Slide): Promise<Slide>
     * @summary Change the slideshow's current slide to a specific slide or index.
     * @description
     *  The `changeSlide()` function returns a Promise that is resolved when
     *  the slideshow finishes moving from the current slide to the slide
     *  referred to by `slideReference`.
     *
     *  You can retrieve [`Slide`]($w.Slide.html) objects to pass to the
     *  `slideReference` parameter from your slideshow using the
     *  [`currentSlide`](#currentSlide) or [`slides`](#slides) properties.
     *
     *  If passing a number, note that slide indices in a slideshow are zero-based,
     *  even though they are numbered starting from 1 in the Editor.
     * @snippet [Slideshow-changeSlide-index.es6=Move to a new slide using an index]
     *  This example moves to the third slide in the slideshow.
     * @snippet [Slideshow-changeSlide-object.es6=Move to a new slide using a [`Slide`]($w.Slide.html) object]
     *  This example moves to the slide that is stored in the `slide` variable.
     * @snippet [Slideshow-changeSlide-done.es6=Moves to a new slide and logs a message when the move is finished]
     * @param {external:Number|$w.Slide} slideReference The slide to move to. Either the index of the slide or a [`Slide`]($w/slide) from the slideshow.
     * @returns {external:Promise}
     * @fulfill {$w.Slide} The slide that the slideshow changed to.
     * @throws {external:Error} If no argument was provided or the argument is not a number or an existing slide.
     * @memberof $w.Slideshow
     */

    changeSlide(slideInfo) {
        return new Promise((resolve, reject) => {
            const compType = members._type.get(this).compType;
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const wixSelector = members._wixSelector.get(this);
            const controllerId = members._controllerId.get(this);

            const slideIndex = computeSlideIndex(RMI, compId, slideInfo);
            if (!validateSlideInfo(compType, slideInfo, getSlidesCount(RMI, compId), slideIndex, this.id)) {
                return reject(generateError.error_slide_input({
                    propertyName: 'slideInfo',
                    functionName: 'changeSlide',
                    minValue: 0,
                    maxValue: getSlidesCount(RMI, compId) - 1,
                    slideshowID: this.id,
                    wrongValue: slideInfo
                }));
            }

            RMI.executeCompBehavior(compId, 'moveToSlide', {slide: slideIndex}, () => {
                const childrenIds = RMI.getChildren(compId);
                const slideCompId = childrenIds[slideIndex];
                const slide = RMI.getComp(slideCompId);
                resolve(RMI, wixSelector.getInstance(RMI, slide.type, slideCompId, controllerId));
            });
        });
    }

    /**
     * @member slides
     * @label property
     * @syntax
       * get slides(): Slide[]
     * @summary Gets a list of all the slides contained in the slideshow.
     * @description
     *  Note that the slide indices in a slideshow are zero-based, even though
     *  they are numbered starting from 1 in the Editor.
     * @snippet [Slideshow-slides.es6=Get a slideshow's slides]
     * @snippet [Slideshow-slides_plus.es6=Get the slides, number of slides, and name of the first slide from a slideshow]
     * @type {$w.Slide[]}
     * @memberof $w.Slideshow
     * @instance
     * @readonly
     */
    get slides() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const wixSelector = members._wixSelector.get(this);
        const controllerId = members._controllerId.get(this);

        return RMI.getChildren(compId).map((id) => wixSelector.getInstance(RMI, RMI.getComp(id).type, id, controllerId));
    }

    /**
     * @member currentIndex
     * @label property
     * @syntax
       * get currentIndex(): Number
     * @summary Gets the index of the slideshow's current slide.
     * @description
     *  Note that the slide indices in a slideshow are zero-based, even though
     *  they are numbered starting from 1 in the Editor.
     * @snippet [Slideshow-currentIndex.es6=Get the name of the current slide's index]
     * @type {external:Number}
     * @see [currentSlide](#currentSlide)
     * @memberof $w.Slideshow
     * @instance
     * @readonly
     */
    get currentIndex() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getState(compId).currentIndex;
    }

    /**
     * @member showNavigationButtons
     * @label property
     * @syntax
     * get showNavigationButtons(): boolean
     * set showNavigationButtons(value: boolean): void
     * @summary Sets or gets whether a slideshow's navigation buttons are shown.
     * @description
     *  Setting the `showNavigationButtons` property to `true` shows
     *  the slideshow's [navigation buttons](https://support.wix.com/en/article/slideshow-layouts).
     *  Setting it to `false` hides the slideshow's [navigation buttons](https://support.wix.com/en/article/slideshow-layouts).
     *
     *  Getting the `showNavigationButtons` property returns
     *  whether a slideshow's [navigation buttons](https://support.wix.com/en/article/slideshow-layouts)
     *  are shown or hidden.
     * @snippet [Slideshow-showNavigationButtons_get.es6=Get whether navigation buttons are shown]
     * @snippet [Slideshow-showNavigationButtons_set.es6=Set navigation buttons to be hidden]
     * @snippet [Slideshow-showNavigationButtons_toggle.es6=Toggle whether navigation buttons are shown]
     * @type {external:Boolean}
     * @memberof $w.Slideshow
     * @instance
     */
    get showNavigationButtons() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getProps(compId).showNavigationButton;
    }

  /**
   * @member showNavigationButtons
   * @memberof $w.Slideshow
   * @param {external:Boolean} show
   */
    set showNavigationButtons(show) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'showNavigationButtons',
            dataToValidate: {showNavigationButtons: show},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setProps(compId, {
            showNavigationButton: show
        });
    }

    /**
     * @member showSlideButtons
     * @label property
     * @syntax
     * get showSlideButtons(): boolean
     * set showSlideButtons(value: boolean): void
     * @summary Set or returns whether a slideshow's slide buttons are shown.
     * @description
     *  Setting the `showSlideButtons` property to `true` shows
     *  the slideshow's [slide buttons](https://support.wix.com/en/article/slideshow-layouts).
     *  Setting it to `false` hides the slideshow's [slide buttons](https://support.wix.com/en/article/slideshow-layouts).
     *
     *  Getting the `showSlideButtons` property returns
     *  whether a slideshow's [slide buttons](https://support.wix.com/en/article/slideshow-layouts)
     *  are shown or hidden.
     * @snippet [Slideshow-showSlideButtons_get.es6=Get whether slide buttons are shown]
     * @snippet [Slideshow-showSlideButtons_set.es6=Set slide buttons to be hidden]
     * @snippet [Slideshow-showSlideButtons_toggle.es6=Toggle whether slide buttons are shown]
     * @type {external:Boolean}
     * @memberof $w.Slideshow
     * @instance
     */
    get showSlideButtons() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getProps(compId).showNavigationDots;
    }

  /**
   * @member showSlideButtons
   * @memberof $w.Slideshow
   * @param {external:Boolean} show
   */
    set showSlideButtons(show) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'showSlideButtons',
            dataToValidate: {showSlideButtons: show},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setProps(compId, {
            showNavigationDots: show
        });
    }

    toJSON() {
        const {id, type, global, rendered, currentIndex, showNavigationButtons, showSlideButtons} = this;
        return {id, type, global, rendered, currentIndex, showNavigationButtons, showSlideButtons};
    }
}

mixin(Slideshow.prototype, HiddenCollapsedMixin.prototype);
mixin(Slideshow.prototype, PlayableMixin.prototype);
mixin(Slideshow.prototype, ClickableMixin.prototype);
mixin(Slideshow.prototype, ContainableMixin.prototype);

setStageUnfriendlyFunctions(Slideshow, ['changeSlide']);


export default Slideshow;
